import { SUPPORTED_APP_TYPE, INTERNAL_APP, PRELOADED_APP } from '../constant';

import { mozAppHelper } from '../helper/mozapp-helper';
import {
  isValidURL,
  normalizedURL,
  localeCode,
  trimBgs,
  trimIcons,
  trimLocales,
  getSize
} from '../helper/application-helper';

import {
  CheckAppUpdateCommand,
  InstallCommand,
  UninstallCommand,
  UpdateAppCommand,
  LaunchAppCommand,
  CancelDownloadCommand,
  ShowToastCommand
} from 'kaistore-post-messenger/src/commands';
import { mozAppEvent as constMozAppEvent } from 'kaistore-post-messenger/lib/constants';
import { Toast } from 'kaistore-post-messenger/src/models';
import { MessageSender } from 'web-message-helper';

import AppStore from '../app-store';

class Application {
  constructor(manifestURL) {
    // trim manifest url to make sure there is no space in it.
    this.manifestURL = encodeURI(manifestURL.trim());
    this._remoteInfo = null;
    this._mozAPP = null;
    this.state = {
      downloading: false,
      updatable: false,
      liked: false,
      pinned: false,
      updatePending: false
    };
    this._trimmedRemoteInfo = {};
    this._trimmedMozAPPInfo = {};
    this.purchased = false;
  }

  get isBookmark() {
    return this.info.type === 'bookmark';
  }

  get isHosted() {
    return this.info.type === 'hosted';
  }

  get isInternal() {
    return INTERNAL_APP.includes(this.id);
  }

  get isPreloaded() {
    return PRELOADED_APP.includes(this.id);
  }

  get isNativeApp() {
    return (
      this.mozAPP && this.mozAPP.manifest && this.mozAPP.manifest.isNativeApp
    );
  }

  get isRebootNeeded() {
    return this.mozAPP && this.mozAPP.manifest && this.mozAPP.manifest.reboot;
  }

  get id() {
    if (this.remoteInfo && this.remoteInfo.id) {
      return this.remoteInfo.id;
    }

    const manifestInfo = this.manifestURL.split('/');
    const lastPart = manifestInfo[manifestInfo.length - 1];
    if (lastPart === 'manifest.webapp') {
      return null;
    }
    return manifestInfo[manifestInfo.length - 1];
  }

  get remoteInfo() {
    return this._remoteInfo;
  }

  get mozAPP() {
    return this._mozAPP;
  }

  get isInRemoteList() {
    return this._remoteInfo !== null;
  }

  get isInstalled() {
    return Boolean(this._mozAPP && this._mozAPP.installState === 'installed');
  }

  get info() {
    const remoteInfo = this._trimmedRemoteInfo;
    const mozAPPInfo = this._trimmedMozAPPInfo;
    return {
      bg: remoteInfo.bg || mozAPPInfo.bg || null,
      icon: remoteInfo.icon || mozAPPInfo.icon || null,
      localized: remoteInfo.localized || mozAPPInfo.localized || {},
      url: remoteInfo.url || null,
      type: remoteInfo.type || mozAPPInfo.type,
      paid: remoteInfo.paid || false,
      productId: remoteInfo.productId || null,
      version: remoteInfo.version || mozAPPInfo.version,
      size: getSize(mozAPPInfo.size) || null
    };
  }

  get brickInfo() {
    return {
      id: this.id,
      isBookmark: this.isBookmark,
      state: this.state,
      isInstalled: this.isInstalled,
      purchased: this.purchased,
      info: this.info
    };
  }

  set remoteInfo(info) {
    this._remoteInfo = info;
    this._trimmedRemoteInfo = this.trimRemoteInfo(info);
  }

  set mozAPP(mozAPP) {
    this._mozAPP = mozAPP;
    if (mozAPP && mozAPP.updateManifest) {
      this._trimmedMozAPPInfo = this.trimMozAPPInfo(mozAPP.updateManifest);
    } else {
      this._trimmedMozAPPInfo = {};
    }
  }

  setRemoteInfo(info) {
    if (!SUPPORTED_APP_TYPE.includes(info.type)) {
      return false;
    }

    if (this.isRemoteInfoValid(info)) {
      this.remoteInfo = info;
      return true;
    }

    return false;
  }

  isRemoteInfoValid(remoteInfo) {
    if (remoteInfo.type === 'bookmark') {
      const URL = remoteInfo.url;
      return URL && isValidURL(URL);
    }
    return remoteInfo.id && remoteInfo.manifest_url;
  }

  trimRemoteInfo(remoteInfo) {
    return {
      bg: trimBgs(remoteInfo.bgs),
      icon: trimIcons(remoteInfo.icons),
      localized: trimLocales(remoteInfo.locales, localeCode(remoteInfo)),
      url: remoteInfo.url ? normalizedURL(remoteInfo.url) : null,
      type: remoteInfo.type,
      paid: remoteInfo.paid || false,
      productId: remoteInfo.product_id || null,
      version: remoteInfo.version || null
    };
  }

  trimMozAPPInfo(updateManifest) {
    return {
      bg: trimBgs(updateManifest.bgs),
      icon: trimIcons(updateManifest.icons),
      localized: trimLocales(
        updateManifest.locales,
        localeCode(updateManifest)
      ),
      type: updateManifest.type,
      version: updateManifest.version || null,
      size: updateManifest.size
    };
  }

  checkUpdate() {
    if (!this.mozAPP) {
      return;
    }
    const manifestURL = this.manifestURL;
    const command = new CheckAppUpdateCommand({
      detail: {
        manifestURL: manifestURL
      }
    });

    MessageSender.send(command);
  }

  updateState(detail) {
    this.state = { ...this.state, ...detail };
    // auto flag updatePending to false when app starts downloading
    if (detail.downloading === true) {
      this.state = { ...this.state, updatePending: false };
    }
    const evt = new CustomEvent('appstore:change', {
      detail: { manifestURL: this.manifestURL }
    });
    window.dispatchEvent(evt);
  }

  install(jwt = null) {
    this.updateState({ downloading: true });

    const command = new InstallCommand({
      detail: {
        isHosted: this.isHosted,
        manifestURL: this.manifestURL,
        jwt: jwt
      }
    });

    return new Promise((resolve, reject) => {
      MessageSender.send(command, (success, detail) => {
        if (success === true) {
          resolve();
        } else {
          reject(detail.error);
        }
      });
    });
  }

  uninstall() {
    const command = new UninstallCommand({
      detail: {
        manifestURL: this.manifestURL
      }
    });
    MessageSender.send(command);
  }

  update() {
    this.updateState({ downloading: true });

    const command = new UpdateAppCommand({
      detail: {
        manifestURL: this.manifestURL
      }
    });
    MessageSender.send(command);
  }

  launch() {
    const command = new LaunchAppCommand({
      detail: {
        manifestURL: this.manifestURL
      }
    });
    MessageSender.send(command);
  }

  cancelDownload() {
    const command = new CancelDownloadCommand({
      detail: {
        manifestURL: this.manifestURL
      }
    });
    MessageSender.send(command);
  }

  handleDownload() {
    this.updateState({
      downloading: true
    });
  }

  handleDownloadError() {
    this.updateState({
      downloading: false
    });
  }

  handleDownloadSuccess() {
    // Remove unused info: description & locales.description
    mozAppHelper.releaseUnusedInfo(this.mozAPP);
    this.updateState({
      downloading: false,
      updatable: false
    });
  }

  handleUpdatedSuccess() {
    this.updateState({
      downloading: false,
      updatable: false,
      nativeUpdated: this.isNativeApp
    });
    AppStore.updatableApps.system.delete(this.manifestURL);
    AppStore.updatableApps.remote.delete(this.manifestURL);
    if (nativeUpdated) {
      AppStore.shouldReboot = true;
    }
  }

  handleInstall(mozAppEvent) {
    if (this.isInstalled) {
      this.handleDownloadSuccess();
      return;
    }

    if (mozAppEvent === constMozAppEvent.ON_DOWNLOAD_APPLIED) {
      this.handleDownloadSuccess();
    } else if (mozAppEvent === constMozAppEvent.ON_DOWNLOAD_ERROR) {
      this.handleDownloadError();
    } else {
      this.handleDownload();
    }
  }

  handleUninstall() {
    // clear up app props.
    this.mozAPP = null;
    this.updateState({
      downloading: false,
      updatable: false
    });
    const messageOption = {
      messageL10nId: 'uninstalled-msg-with-name',
      messageL10nArgs: {
        appName: this.info.localized.name
      }
    };

    MessageSender.send(
      new ShowToastCommand({
        detail: new Toast(messageOption)
      })
    );
  }

  handleUpdate(mozAppEvent) {
    switch (mozAppEvent) {
      case constMozAppEvent.ON_DOWNLOAD_AVAILABLE:
        AppStore.syncUpdatableApp(this);
        break;
      case constMozAppEvent.ON_DOWNLOAD_APPLIED:
        this.handleUpdatedSuccess();
        break;
      case constMozAppEvent.ON_DOWNLOAD_SUCCESS:
        this.handleDownloadSuccess();
        break;
      case constMozAppEvent.ON_DOWNLOAD_ERROR:
        this.handleDownloadError();
        break;
    }
  }

  handleBookmarkUpdate(pin) {
    this.updateState({ pinned: pin });
    const messageL10nId = pin
      ? 'pin-bookmark-completely'
      : 'unpin-bookmark-completely';
    MessageSender.send(
      new ShowToastCommand({
        detail: new Toast({ messageL10nId: messageL10nId })
      })
    );
  }
}

export default Application;



/** WEBPACK FOOTER **
 ** ./src/model/Application.js
 **/